import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialDashboardCardsState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const { dashboardCardsEndPoint } = endPoints;

export const fetchAllCards = createAsyncThunk(
  "dashboardCards/fetchAllCards",
  async (
    { searchParamsObj = {} }: { searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: 1,
      endpoint: dashboardCardsEndPoint,
      searchParams: { ...searchParamsObj },
      limit: 100,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addCard = createAsyncThunk(
  "dashboardCards/addCard",
  async (cardData: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${dashboardCardsEndPoint}/`,
        cardData
      );

      if (response.status === 201) {
        NotificationManager.success("Card Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCard = createAsyncThunk(
  "dashboardCards/deleteCard",
  async ({ cardId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        dashboardCardsEndPoint,
        cardId
      );

      if (response.status === 204) {
        NotificationManager.success("Card Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateCard = createAsyncThunk(
  "dashboardCards/updateCard",
  async ({ updatedData, cardId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        dashboardCardsEndPoint,
        cardId,
        updatedData
      );
      if (response.status === 204) {
        NotificationManager.success("Card updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const dashboardCardSlice = createSlice({
  name: "dashboardCards",
  initialState: initialDashboardCardsState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all user Widgets
    builder
      .addCase(fetchAllCards.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllCards.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.cardsArray = action.payload.objects.sort((a: any, b: any) =>
            a?.chart_type.localeCompare(b?.chart_type)
          ) as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllCards.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.cardsArray = [];
          state.meta = defaultMetaObj;
        }
      );

    // add Widget
    builder
      .addCase(addCard.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addCard.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addCard.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete Widget
    builder
      .addCase(deleteCard.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteCard.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteCard.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //   update Widget
    builder
      .addCase(updateCard.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateCard.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateCard.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const dashboardCardDataReducer = dashboardCardSlice.reducer;
